import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { MeetingModelJson } from "@performyard/service-contracts";

import Spacer from "../../../../components/Spacer";
import ComponentLoader from "../../../../components/Spinner/ComponentLoader";
import UserAssignmentModal from "../components/ChecklistItems/UserAssignmentModal";
import { CreateMeetingModal, EditMeetingModal } from "../components/Meetings/modals";
import WorkspaceDetailsBreadcrumbs from "../components/WorkspaceDetailsBreadcrumbs";
import WorkspaceDetailsEmptyState from "../components/WorkspaceDetailsEmptyState";
import WorkspaceDetailsFilterPanel from "../components/WorkspaceDetailsFilterPanel";
import WorkspaceDetailsHeader from "../components/WorkspaceDetailsHeader";
import WorkspaceDetailsMeetingList from "../components/WorkspaceDetailsMeetingList";

import { useCurrentUser, useMeetingWorkspaceDetails, useMeetingsSearch } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import useForm from "../../../../hooks/useForm";
import { useWorkspaceDetailsLogic } from "../hooks";

import { queryClient } from "../../../../api/queryClient";

import { convertFiltersToSearchParams } from "../utils";

import BasePageTemplate from "../../../../pageTemplates/BasePageTemplate";
import useInfoPageTemplateQueryState from "../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../../../Error";
import { config } from "../config";
import { type WorkspaceDetailsFilters, WorkspaceDetailsFiltersSchema } from "../schema";

const WorkspaceDetails = () => {
	const { workspaceId } = useParams();
	const location = useLocation();
	const previousSearchParams = new URLSearchParams(location.search).get("from") || "";

	const { data: currentUser } = useCurrentUser();
	const { data: workspace } = useMeetingWorkspaceDetails(workspaceId || "");

	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

	// Query state for the page (filters, sort, page)
	const { filters, setFilters, sort, pageNumber, setPageNumber } =
		useInfoPageTemplateQueryState<WorkspaceDetailsFilters>({
			defaultSortName: config.defaultSortName,
			defaultSortDirection: config.defaultSortDirection,
			defaultPageNumber: config.defaultPageNumber,
			fallbackFilters: config.fallbackFilters,
		});

	const userId = currentUser?.me?.authorization?.payload.user_id || "";

	// Meetings search service
	const {
		data: meetings,
		totalCount: totalMeetingsCount,
		isLoading: isFetchingMeetings,
	} = useMeetingsSearch<"workspaceDetails">(
		convertFiltersToSearchParams(filters, sort, pageNumber, workspaceId || "", userId)
	);

	// Filter form state for the filter panel slideout
	const {
		state: filterFormState,
		updateForm: updateFilterForm,
		setInitialState: setInitialFilterForm,
	} = useForm({
		schema: WorkspaceDetailsFiltersSchema,
		defaultValues: filters,
		disableWarning: true,
	});

	const { value: ffMeetings } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	const [isCreateMeetingModalOpen, setIsCreateMeetingModalOpen] = useState(false);
	const [editingMeeting, setEditingMeeting] = useState<MeetingModelJson | null>(null);
	const [userAssignment, setUserAssignment] = useState<{
		actionItemId: string;
		meetingId: string;
		currentAssigneeId?: string;
		dueDate?: Date | null;
		name?: string;
	} | null>(null);

	// Use shared logic hook
	const {
		expandedMeetingIds,
		setExpandedMeetingIds,
		handleToggleAccordion,
		transformWorkspaceParticipants,
		calculateActiveFiltersCount,
		getHasFiltersApplied,
	} = useWorkspaceDetailsLogic(meetings);

	// Only show loading for meetings when filters change, since initial data is prefetched
	if (isFetchingMeetings) {
		return <ComponentLoader />;
	}

	// Show access denied page if feature flag is disabled
	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	// Transform workspace participants for AvatarGroup
	const workspaceParticipants = transformWorkspaceParticipants(workspace?.participants);

	// Count active filters for the filter button badge
	const activeFiltersCount = calculateActiveFiltersCount(filters);

	// Determine if any filters are applied
	const hasFiltersApplied = getHasFiltersApplied(filters);

	const handleAddMeetingClick = () => {
		queryClient.invalidateQueries({
			queryKey: ["get-meeting-carry-over-items-by-workspace"],
		});
		setIsCreateMeetingModalOpen(true);
	};

	return (
		<>
			<BasePageTemplate title={workspace?.name || "Meeting Workspace"}>
				{/* Breadcrumbs */}
				<WorkspaceDetailsBreadcrumbs
					workspaceName={workspace?.name || ""}
					workspaceId={workspaceId || ""}
					previousSearchParams={previousSearchParams}
				/>

				{/* Header with workspace name, participants, and action buttons */}
				<WorkspaceDetailsHeader
					workspaceName={workspace?.name || ""}
					workspaceParticipants={workspaceParticipants}
					activeFiltersCount={activeFiltersCount}
					onFilterClick={() => setIsFilterPanelOpen(true)}
					onAddMeetingClick={handleAddMeetingClick}
					hasExistingMeetings={meetings?.length > 0}
					workspaceId={workspaceId || ""}
					filters={filters}
					sort={sort}
					meetings={meetings}
					isAdmin={false}
				/>

				<Spacer height={2} />

				{/* Main content */}
				{meetings && meetings.length > 0 ? (
					<WorkspaceDetailsMeetingList
						meetings={meetings}
						workspace={workspace!}
						expandedMeetingIds={expandedMeetingIds}
						handleToggleAccordion={handleToggleAccordion}
						userId={userId}
						totalMeetingsCount={totalMeetingsCount}
						pageNumber={pageNumber}
						setPageNumber={setPageNumber}
						pageSize={config.pageSize}
						setEditingMeeting={setEditingMeeting}
						setUserAssignment={setUserAssignment}
					/>
				) : (
					<WorkspaceDetailsEmptyState hasFiltersApplied={hasFiltersApplied} />
				)}
			</BasePageTemplate>

			{/* Filter panel */}
			<WorkspaceDetailsFilterPanel
				isOpen={isFilterPanelOpen}
				onClose={() => setIsFilterPanelOpen(false)}
				onApplyFilters={(filters) => {
					setFilters(filters);
					setInitialFilterForm(filters, true);
				}}
				filters={filterFormState}
				updateForm={updateFilterForm}
			/>

			{/* Modals */}
			{workspace && (
				<>
					<CreateMeetingModal
						isOpen={isCreateMeetingModalOpen}
						handleClose={() => setIsCreateMeetingModalOpen(false)}
						workspace={workspace}
						workspaceHasMeetings={meetings?.length > 0}
						setExpandedMeetingIds={setExpandedMeetingIds}
					/>
					<EditMeetingModal
						isOpen={Boolean(editingMeeting)}
						handleClose={() => setEditingMeeting(null)}
						meeting={editingMeeting}
					/>
					<UserAssignmentModal
						isOpen={Boolean(userAssignment)}
						onClose={() => setUserAssignment(null)}
						assignerId={userId}
						assignmentContext={userAssignment}
						workspace={workspace}
					/>
				</>
			)}
		</>
	);
};

export default WorkspaceDetails;
